import {useCallback, useEffect, useState} from 'react';
import {Button, Icon, Link, Typography} from '@ezcater/tapas';
import {
  faCircleMinus,
  faClock,
  faDiamondExclamation,
  faEnvelope,
  faLocationDot,
  faMessage,
  faMobile,
  faPhone,
  faQuestionCircle,
} from '@fortawesome/pro-regular-svg-icons';
import Trans from 'next-translate/Trans';
import useTranslation from 'next-translate/useTranslation';
import {twJoin} from 'tailwind-merge';

import useEzCaterCompanyInfo from '@/hooks/useEzCaterCompanyInfo';
import {useHydrated} from '@/hooks/useHydrated';
import useIdentity from '@/hooks/useIdentity';
import useSelfServiceReportingModal from '@/hooks/useSelfServiceReportingModal';
import useTracking from '@/hooks/useTracking';
import useWebchatEntryPoint from '@/hooks/useWebchatEntryPoint';
import {CUSTOMER_HELP_CENTER_URL, PRIVACY_PATH, TERMS_PATH} from '@/paths';
import {gtm} from '@/utils';
import {contactUsClickPayload} from '@/utils/googleAnalyticsEvents';
import trackChatStarted from '@/utils/trackChatStarted';
import SupportMessageModal, {
  useSubmitSupportCase,
  useSupportMessageEnabled,
} from './SupportMessage';

type ContactUsInfoPageProps = {
  onClose?: () => void;
  className?: string;
  experimentVariation: string;
};

const trackGTMEvent = (contactType: string) => {
  gtm.dataLayer(contactUsClickPayload({contactType}));
};

const ContactUsInfoPage = ({
  onClose,
  className = '',
  experimentVariation,
}: ContactUsInfoPageProps): React.ReactElement => {
  const {t} = useTranslation('common');
  const {openWebchat, isMobileWebchat, webchatHref} = useWebchatEntryPoint();
  const companyInfo = useEzCaterCompanyInfo();

  const {track} = useTracking();

  // CSP+Cloudflare prevents us from rendering `mailto:` links in our initial HTML
  const canRenderEmailLink = useHydrated();

  useEffect(() => {
    track('shown-contact-us', {
      page: window.location.pathname,
      misc_json: JSON.stringify({variant: 'page', experiment_variation: experimentVariation}),
    });
  }, [track, experimentVariation]);

  const trackMethodSelected = useCallback(
    (methodType: string) => {
      // GTM tracking - for basic event capture (no user attribution)
      trackGTMEvent(methodType === 'sms' ? 'text' : methodType);

      // useTracking - captures user.tracking_id for attribution and warehouse analytics
      // GTM events don't include tracking IDs and don't reach the warehouse,
      // so we need ezCater events for proper user attribution and comprehensive analytics
      track('clicked-contact-us-link', {
        page: window.location.pathname,
        misc_json: JSON.stringify({
          method: methodType,
          variant: 'page',
          experiment_variation: experimentVariation,
        }),
      });
    },
    [track, experimentVariation],
  );

  const trackSelfServiceIssueModal = () => {
    track('self-service-reporting-modal-opened', {
      page: window.location.pathname,
    });
  };
  const {toggleSelfServiceProviderModal} = useSelfServiceReportingModal();
  const {data, isLoggedIn} = useIdentity();

  const showSupportMessageWizard = useSupportMessageEnabled();
  const [isSupportMessageOpen, setIsSupportMessageOpen] = useState(false);
  const submitSupportCase = useSubmitSupportCase();

  const toggleAndTrackSelfServiceIssueModal = () => {
    trackSelfServiceIssueModal();
    trackMethodSelected('self-service');
    toggleSelfServiceProviderModal();
    onClose?.();
  };

  const toggleLibertyChat = () => {
    trackMethodSelected('chat');
    openWebchat();
    onClose?.();
  };

  const trackAndCloseForMobileChat = () => {
    trackChatStarted();
    trackMethodSelected('chat');
    onClose?.();
  };

  return (
    <div className={twJoin('space-y-8', className)}>
      {/* Hero Section */}
      <div className="text-center">
        <Typography
          render={<h1>{t('components.ContactUs.info.title')}</h1>}
          variant="display-lg"
          className="mb-4"
        />
        <p className="mb-12 text-lg text-peppercorn-600">
          {t('components.ContactUs.info.description')}
        </p>
      </div>
      {/* Contact Methods Grid */}
      <div className="mx-auto grid max-w-4xl grid-cols-1 gap-8 tablet:grid-cols-2">
        {/* Address */}
        <div className="flex items-center rounded-2xl border p-6 transition-shadow hover:shadow-md">
          <Icon className="mr-4 text-green-400" icon={faLocationDot} size="medium" />
          <div className="font-medium leading-tight">
            <div className="font-bold">{t('components.ContactUs.info.addressLine1')}</div>
            <div>{t('components.ContactUs.info.addressLine2')}</div>
            <div>{t('components.ContactUs.info.addressLine3')}</div>
          </div>
        </div>

        {/* Chat */}
        <div className="flex items-center rounded-2xl border p-6 transition-shadow hover:shadow-md">
          <Icon className="mr-4 text-green-400" icon={faMessage} size="medium" />
          <div className="leading-tight">
            <p className="mb-1 font-semibold">{t('components.ContactUs.info.chat')}</p>
            {isMobileWebchat ? (
              <a
                className="font-bold"
                data-testid="contact-us-chat-link"
                href={webchatHref}
                onClick={trackAndCloseForMobileChat}
                target="_blank"
                rel="noopener noreferrer"
              >
                {t('components.ContactUs.info.chatLink')}
              </a>
            ) : (
              <Button
                data-testid="contact-us-chat-button"
                onClick={() => {
                  trackChatStarted();
                  toggleLibertyChat();
                }}
                variant="buttonLink"
              >
                {t('components.ContactUs.info.chatLink')}
              </Button>
            )}
          </div>
        </div>

        {/* Phone */}
        <div className="flex items-center rounded-2xl border p-6 transition-shadow hover:shadow-md">
          <Icon className="mr-4 text-green-400" icon={faPhone} size="medium" />
          <div className="leading-tight">
            <p id="contact-us-phone-label" className="mb-1 font-semibold">
              {t('components.ContactUs.info.call')}
            </p>
            <a
              aria-labelledby="contact-us-phone-label"
              className="font-bold"
              href={`tel:${companyInfo.supportPhoneNumber.replace(/[^0-9]/g, '')}`}
              onClick={() => trackMethodSelected('phone')}
            >
              {companyInfo.supportPhoneNumber}
            </a>
          </div>
        </div>

        {/* SMS */}
        <div className="rounded-2xl border p-6 transition-shadow hover:shadow-md">
          <div className="flex items-center">
            <Icon className="mr-4 text-green-400" icon={faMobile} size="medium" />
            <div className="leading-tight">
              <p id="contact-us-sms-label" className="mb-1 font-semibold">
                {t('components.ContactUs.info.text')}
              </p>
              <a
                aria-labelledby="contact-us-sms-label"
                className="font-bold"
                href={`sms:${companyInfo.supportSmsNumber}`}
                onClick={() => trackMethodSelected('sms')}
              >
                {companyInfo.supportSmsDisplayNumber}
              </a>
            </div>
          </div>
          <p className="mt-3 text-xs leading-snug text-peppercorn-600">
            <Trans
              i18nKey="common:components.ContactUs.info.smsConsentDisclaimer"
              components={{
                PrivacyLink: <Link href={PRIVACY_PATH} target="_blank" rel="noopener noreferrer" />,
                TermsLink: <Link href={TERMS_PATH} target="_blank" rel="noopener noreferrer" />,
                SmsTermsLink: (
                  <Link
                    href="https://ezcater.com/sms-terms"
                    target="_blank"
                    rel="noopener noreferrer"
                  />
                ),
              }}
            />
          </p>
        </div>

        {/* Email */}
        <div className="flex items-center rounded-2xl border p-6 transition-shadow hover:shadow-md">
          <Icon className="mr-4 text-green-400" icon={faEnvelope} size="medium" />
          <div className="leading-tight">
            <p id="contact-us-email-label" className="mb-1 font-semibold">
              {t('components.ContactUs.info.email')}
            </p>
            {showSupportMessageWizard ? (
              <Button
                className="pl-0 font-bold"
                onClick={() => {
                  track('support-message-modal-opened', {
                    page: window.location.pathname,
                    misc_json: JSON.stringify({
                      is_logged_in: isLoggedIn,
                      surface: 'contact_us_page',
                    }),
                  });
                  setIsSupportMessageOpen(true);
                }}
                variant="buttonLink"
              >
                {t('components.SupportMessage.button')}
              </Button>
            ) : canRenderEmailLink ? (
              <a
                aria-labelledby="contact-us-email-label"
                className="block font-bold"
                href={`mailto:${companyInfo.supportEmailAddress}`}
                onClick={() => {
                  trackMethodSelected('email');
                }}
              >
                {companyInfo.supportEmailAddress}
              </a>
            ) : (
              <div className="h-5 w-44 animate-pulse rounded-md bg-peppercorn-200" />
            )}
          </div>
        </div>

        {/* Customer Help Center */}
        <div className="flex items-center rounded-2xl border p-6 transition-shadow hover:shadow-md">
          <Icon className="mr-4 text-green-400" icon={faQuestionCircle} size="medium" />
          <div className="leading-tight">
            <p className="mb-1 font-semibold">
              {t('components.ContactUs.info.customerHelpCenter')}
            </p>
            <a
              className="font-bold"
              href={CUSTOMER_HELP_CENTER_URL}
              onClick={() => trackMethodSelected('help-center')}
              target="_blank"
              rel="noopener noreferrer"
            >
              {t('components.ContactUs.info.visitHelpCenter')}
            </a>
          </div>
        </div>

        {/* Unsubscribe Link */}
        <div className="flex items-center rounded-2xl border p-6 transition-shadow hover:shadow-md">
          <Icon className="mr-4 text-green-400" icon={faCircleMinus} size="medium" />
          <div className="leading-tight">
            <p className="mb-1 font-semibold">{t('components.ContactUs.info.physicalMailing')}</p>
            <a
              className="font-bold"
              href={companyInfo.physicalMailUnsubscribeURL}
              onClick={() => trackMethodSelected('unsubscribe')}
              target="_blank"
              rel="noopener noreferrer"
            >
              {t('components.ContactUs.info.unsubscribeHere')}
            </a>
          </div>
        </div>

        {/* Self-Service Reporting (Logged-in users only) */}
        {isLoggedIn && (
          <div className="flex items-center rounded-2xl border p-6 transition-shadow hover:shadow-md">
            <Icon className="mr-4 text-green-400" icon={faDiamondExclamation} size="medium" />
            <div className="leading-tight">
              <p className="mb-1 font-semibold">{t('components.ContactUs.info.orderProblems')}</p>
              <Button onClick={toggleAndTrackSelfServiceIssueModal} variant="buttonLink">
                {t('components.ContactUs.info.reportAnIssue')}
              </Button>
            </div>
          </div>
        )}
      </div>
      {/* Available Hours */}
      <div className="flex items-center justify-center border-t pt-8">
        <Icon className="mr-4 text-peppercorn-400" icon={faClock} size="medium" />
        <span className="text-lg">{t('components.ContactUs.info.availableHours')}</span>
      </div>

      {showSupportMessageWizard && (
        <SupportMessageModal
          fieldContext={{
            fullName: data?.me?.consumerAccount?.fullName ?? '',
            email: data?.me?.consumerAccount?.email ?? '',
          }}
          onClose={() => setIsSupportMessageOpen(false)}
          onSubmit={submitSupportCase}
          open={isSupportMessageOpen}
          surface="contact_us_page"
        />
      )}
    </div>
  );
};

export default ContactUsInfoPage;
